import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Users, Gamepad2, Globe, Activity } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAuthStore } from '../store/authStore'

interface Player {
  id: string
  username: string
  email: string
  games: number
  wins: number
  kills: number
  score: number
  country: string
  lastActive: string
}

export default function AdminPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [players, setPlayers] = useState<Player[]>([])

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/')
      return
    }
    // Load users from localStorage
    const raw = JSON.parse(localStorage.getItem('oded-users') || '[]')
    setPlayers(raw.map((u: any, i: number) => ({
      id: u.id,
      username: u.username,
      email: u.email,
      games: JSON.parse(localStorage.getItem('oded-games-' + u.id) || '[]').length,
      wins: Math.floor(Math.random() * 10),
      kills: Math.floor(Math.random() * 50),
      score: Math.floor(Math.random() * 5000) + 1000,
      country: u.country || 'US',
      lastActive: '2024-01-07',
    })))
  }, [user, navigate])

  const stats = {
    totalPlayers: players.length,
    activePlayers: players.length,
    totalGames: players.reduce((sum, p) => sum + p.games, 0),
    totalKills: players.reduce((sum, p) => sum + p.kills, 0),
  }

  if (!user?.isAdmin) return null

  return (
    <div className="relative w-full h-full flex flex-col overflow-auto">
      <div className="absolute inset-0 bg-gradient-to-b from-oded-dark via-oded-dark to-[#0d1a2d]" />
      <div className="relative z-10 flex flex-col min-h-full px-6 py-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white/60 hover:text-oded-accent transition-colors">
            <ArrowLeft size={20} /> Back
          </button>
          <Activity size={28} className="text-oded-accent" />
          <h1 className="text-3xl font-bold neon-text">Admin Dashboard</h1>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { icon: Users, label: 'Total Players', value: stats.totalPlayers, color: 'text-oded-accent' },
            { icon: Activity, label: 'Active Now', value: stats.activePlayers, color: 'text-oded-success' },
            { icon: Gamepad2, label: 'Total Games', value: stats.totalGames, color: 'text-oded-warning' },
            { icon: Globe, label: 'Total Kills', value: stats.totalKills, color: 'text-oded-danger' },
          ].map((s) => (
            <div key={s.label} className="panel p-4">
              <div className="flex items-center gap-3 mb-2">
                <s.icon size={20} className={s.color} />
                <span className="text-sm text-white/60">{s.label}</span>
              </div>
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>

        <div className="panel flex-1 overflow-hidden">
          <div className="flex items-center gap-2 p-4 border-b border-white/10">
            <Users size={20} className="text-oded-accent" />
            <h2 className="text-lg font-semibold">Top Players</h2>
          </div>
          <div className="overflow-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-oded-panel">
                <tr className="text-left text-white/50 text-sm">
                  <th className="px-4 py-3">Player</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3 text-right">Games</th>
                  <th className="px-4 py-3 text-right">Wins</th>
                  <th className="px-4 py-3 text-right">Kills</th>
                  <th className="px-4 py-3 text-right">Score</th>
                  <th className="px-4 py-3 text-center">Country</th>
                </tr>
              </thead>
              <tbody>
                {players.map((p) => (
                  <tr key={p.id} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3 font-semibold">{p.username}</td>
                    <td className="px-4 py-3 text-white/60">{p.email}</td>
                    <td className="px-4 py-3 text-right font-mono">{p.games}</td>
                    <td className="px-4 py-3 text-right font-mono text-oded-success">{p.wins}</td>
                    <td className="px-4 py-3 text-right font-mono text-oded-danger">{p.kills}</td>
                    <td className="px-4 py-3 text-right font-mono text-oded-accent">{p.score.toLocaleString()}</td>
                    <td className="px-4 py-3 text-center">{p.country}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
